import { useState } from 'react'

export interface SeededModalField<T> {
  value: T
  setValue: (value: T) => void
  /** Call from the modal's own close handler - forces the next open, even
   * one with the same openKey (re-opening "Duplicate" on the same row twice
   * without opening anything else in between), to reseed from scratch
   * rather than keep whatever was typed and abandoned last time. */
  reset: () => void
}

// A form-hosting modal instance stays mounted across separate open cycles
// (only its `open`/`source` props change), so a plain useState initializer
// would only ever run once. `openKey` changes identity each time a new open
// cycle starts - null while closed, '__new__' for a fresh create, or the
// duplicate source's own id/name - and whenever it changes to a non-null
// value, `seed()` produces that cycle's starting value exactly once (the
// same conditional-setState-in-render pattern the detail screens use to
// derive state from changing inputs). PRD 14. Screens that edit a copy of
// server data and must follow it when it changes use hooks/useServerSeed.ts.
export function useSeededModalField<T>(openKey: string | null, seed: () => T): SeededModalField<T> {
  const [value, setValue] = useState<T>(seed)
  const [initializedFor, setInitializedFor] = useState<string | null>(null)
  if (openKey !== null && initializedFor !== openKey) {
    setValue(seed())
    setInitializedFor(openKey)
  }
  return { value, setValue, reset: () => setInitializedFor(null) }
}
